import { OverlaySpinner } from "~/atoms/spinner";
import NoResult from "~/molecules/NoResult";
import { useTranslation } from "react-i18next";
import { FormProvider } from "react-hook-form";
import { Card, CardContent, CardFooter } from "~/atoms/card";
import { UnsavedChangesAlert } from "~/molecules/UnsavedChangesAlert";
import { ConfirmDialog } from "~/molecules/ConfirmDialog";
import { WorkflowPageFooter } from "~/pages/workflow-editor/components/WorkflowPageFooter";
import { WorkflowPageHeader } from "~/pages/workflow-editor/components/WorkflowPageHeader";
import { WorkflowMetadataSection } from "~/pages/workflow-editor/components/WorkflowMetadataSection";
import { WorkflowStepsSection } from "~/pages/workflow-editor/components/WorkflowStepsSection";
import { useWorkflowEditorPage } from "~/pages/workflow-editor/useWorkflowEditorPage";
import { useState } from "react";
import { useNavigate } from "react-router";
import { cn } from "~/lib/utils";

export default function WorkflowPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showApplyConfirm, setShowApplyConfirm] = useState(false);

  const {
    isLoading,
    mode,
    selectedTemplateId,
    isApplying,
    showTemplateDropdown,
    templateOptions,
    form,
    hasUnsavedChanges,
    hasDateOrderError,
    handleTemplateSelect,
    handleApplyTemplate,
    handleCancelPreview,
    setEditedSteps,
    handleSave,
  } = useWorkflowEditorPage();

  if (isLoading) return <OverlaySpinner />;
  if (!mode) return <NoResult />;

  const isPreviewing = mode.type === "previewing";

  return (
    <FormProvider {...form}>
      <div className="container flex grow flex-col gap-6 py-6">
        <WorkflowPageHeader
          release={mode.release}
          isPreviewMode={isPreviewing}
          isApplying={isApplying}
          hasUnsavedChanges={hasUnsavedChanges}
          showTemplateDropdown={showTemplateDropdown}
          selectedTemplateId={selectedTemplateId}
          templateOptions={templateOptions}
          onCancelPreview={handleCancelPreview}
          onApplyTemplate={handleApplyTemplate}
          onRequestApplyConfirm={() => setShowApplyConfirm(true)}
          onTemplateSelect={handleTemplateSelect}
        />

        <Card>
          <CardContent className="pt-6">
            <div
              className={cn("space-y-2", {
                "rounded-lg border-2 border-dashed p-4": isPreviewing,
              })}
            >
              <WorkflowMetadataSection mode={mode} />
              <WorkflowStepsSection mode={mode} setSteps={setEditedSteps} />
            </div>
          </CardContent>

          {mode.type === "editing" && (
            <CardFooter>
              <WorkflowPageFooter
                hasUnsavedChanges={hasUnsavedChanges}
                hasDateOrderError={hasDateOrderError}
                onCancel={() => navigate(-1)}
                onSave={handleSave}
              />
            </CardFooter>
          )}
        </Card>

        <UnsavedChangesAlert
          hasUnsavedChanges={hasUnsavedChanges && !isPreviewing}
          title={t(($) => $.commons.unsavedChanges.workflow.title)}
          description={t(($) => $.commons.unsavedChanges.workflow.description)}
          cancelLabel={t(($) => $.commons.unsavedChanges.continueEditing)}
          confirmLabel={t(($) => $.commons.unsavedChanges.discardChanges)}
        />

        <ConfirmDialog
          open={showApplyConfirm}
          onOpenChange={setShowApplyConfirm}
          title={t(
            ($) => $.workflowTemplates.applyOnWorkflow.unsavedChangesTitle,
          )}
          description={t(
            ($) =>
              $.workflowTemplates.applyOnWorkflow.unsavedChangesDescription,
          )}
          confirmLabel={t(
            ($) => $.workflowTemplates.applyOnWorkflow.unsavedChangesConfirm,
          )}
          onConfirm={handleApplyTemplate}
        />
      </div>
    </FormProvider>
  );
}
